import type { MenuItem } from '@/types/menu'

/**
 * Price resolution priority:
 * 1. customizationPrices[rowIngredientId] — always charged, skips free_quantity counting
 * 2. rowPricingRules[rowId].price — respects freeQuantity (index < freeQuantity → free)
 * 3. 0 (included in base price)
 */

/**
 * Get the static base price for an ingredient (for display when no dynamic context).
 * Returns the override price if one exists, or the row rule price, or 0.
 */
export function getIngredientBasePrice(
  menuItem: MenuItem,
  rowId: string,
  rowIngredientId: string
): number {
  // Priority 1: per-ingredient override
  const overridePrice = menuItem.customizationPrices?.[rowIngredientId]
  if (overridePrice !== undefined) return overridePrice

  // Priority 2: row pricing rule (static price, ignoring free_quantity here)
  const rowRule = menuItem.rowPricingRules?.[rowId]
  if (rowRule) return rowRule.price

  // Priority 3: included
  return 0
}

/**
 * Check if a row has dynamic pricing (free_quantity > 0).
 * When true, prices depend on selection order and can't be resolved statically.
 */
export function rowHasDynamicPricing(
  menuItem: MenuItem,
  rowId: string
): boolean {
  const rowRule = menuItem.rowPricingRules?.[rowId]
  return (rowRule?.freeQuantity ?? 0) > 0
}

/**
 * Resolve the price for an ingredient at a specific selection position.
 * Used at runtime when free_quantity makes pricing order-dependent.
 *
 * @param selectionIndex - 0-based position among non-override selections in this row
 */
export function resolveIngredientPrice(
  menuItem: MenuItem,
  rowId: string,
  rowIngredientId: string,
  selectionIndex: number
): number {
  // Priority 1: per-ingredient override — always charged regardless of free slots
  const overridePrice = menuItem.customizationPrices?.[rowIngredientId]
  if (overridePrice !== undefined) return overridePrice

  // Priority 2: row pricing rule with free_quantity
  const rowRule = menuItem.rowPricingRules?.[rowId]
  if (rowRule) {
    if (selectionIndex < rowRule.freeQuantity) return 0
    return rowRule.price
  }

  // Priority 3: included
  return 0
}

/**
 * Calculate total price for all selections in a row, accounting for free_quantity.
 * Override ingredients are charged first (they don't consume free slots).
 * Remaining non-override ingredients fill free slots, then pay the row price.
 */
export function calculateRowTotal(
  menuItem: MenuItem,
  rowId: string,
  selectionIds: string[]
): number {
  if (selectionIds.length === 0) return 0

  let total = 0
  let nonOverrideIndex = 0

  for (const id of selectionIds) {
    const overridePrice = menuItem.customizationPrices?.[id]
    if (overridePrice !== undefined) {
      // Override ingredients always pay their price, don't consume free slots
      total += overridePrice
    } else {
      total += resolveIngredientPrice(menuItem, rowId, id, nonOverrideIndex)
      nonOverrideIndex++
    }
  }

  return total
}

/**
 * Get the combo option price override, or fall back to the option's original price.
 */
export function getComboOptionPrice(
  menuItem: MenuItem,
  optionId: string,
  fallback: number
): number {
  const overridePrice = menuItem.comboOptionPrices?.[optionId]
  return overridePrice !== undefined ? overridePrice : fallback
}
